import { useMemo } from 'react';

import { AGGREGATE_NAMES, CHAT_COMMANDS } from 'cpool-constants';

import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useLead from '~/features/leadDetails/hooks/useLead';
import { useGetOwnCorporation } from '~/features/userStructures/hooks/corporation/useGetOwnCorporation';
import { useStartChatMutation } from '~/features/whatsapp/hooks/useStartChatMutation';
import { findOwnDivisionPhoneNumberId } from '~/features/whatsapp/utils/findOwnDivisionPhoneNumberId';
import {
    resolveApplicantStartChat,
    type ResolveApplicantStartChatResult,
} from '~/features/whatsapp/utils/resolveApplicantStartChat';
import useUserInfo from '~/hooks/useUserInfo';

export type ApplicantStartChatSlot = ResolveApplicantStartChatResult & {
    /** True while data this slot's eligibility depends on is still being fetched — the lead
     *  (only when no `preloadedLead` was supplied) and/or the own-corporation lookup used for
     *  `phoneNumberId` (always a live fetch here, preloaded lead or not). Callers that resolve
     *  `missingReason`/`payload` into a blocked-reason UI should wait for this to go `false`
     *  first, otherwise a cold fetch can look identical to "not eligible". */
    isLoading: boolean;
    start: () => Promise<{ chatId: string }>;
};

type PreloadedLeadApplicant = {
    dataProtectionAgreed?: boolean | null;
    personendaten: { telefonPrivat?: string | null };
};

/** Minimal structural subset of a lead this hook reads — lets callers that already have a lead
 *  loaded (e.g. a lead-list row) pass it straight through instead of triggering a re-fetch. */
export type PreloadedLead = {
    antragsteller1?: PreloadedLeadApplicant | null;
    antragsteller2?: PreloadedLeadApplicant | null;
    coApplicantHidden?: boolean | null;
};

const EMPTY_RESULT: ResolveApplicantStartChatResult = { missingReason: null, payload: null };

/**
 * Shared eligibility + mutation for starting a chat with either applicant slot of a lead.
 * Pulls `useLead`, `useGetOwnCorporation`, `useAclPermissions`, `useUserInfo`, and
 * `useStartChatMutation` so callers never duplicate them. `start()` is only called from the
 * open-path (`useOpenLeadWhatsAppChat`) and `ChatView`'s KN2 auto-start effect —
 * `ChatBlockedReason` only reads `missingReason` to render the blocked-reason copy, it never
 * starts a chat itself.
 *
 * When the caller already has a loaded lead (e.g. a lead-list row), pass it as `preloadedLead`
 * so this hook skips its own `useLead` fetch (and the nested `useAclPermissions` call inside it)
 * entirely. Callers without one (e.g. `ChatView`) omit it and fall back to fetching via `useLead`.
 */
export const useApplicantStartChat = (
    loanRequestId: string | undefined,
    preloadedLead?: PreloadedLead,
): { kn1: ApplicantStartChatSlot; kn2: ApplicantStartChatSlot } => {
    const leadQuery = useLead(preloadedLead ? undefined : loanRequestId);
    const lead = preloadedLead ?? leadQuery.data;
    const corporationQuery = useGetOwnCorporation();
    const corporation = corporationQuery.data;
    // useLead is skipped (and so never "loading") once a preloadedLead is supplied, but
    // useGetOwnCorporation always runs here regardless — it's still a cold fetch on first mount
    // even when the lead itself is preloaded, and phoneNumberId comes from it.
    const isLoading = Boolean((!preloadedLead && leadQuery.isLoading) || corporationQuery.isLoading);
    const user = useUserInfo();
    const { permissions } = useAclPermissions();
    const startChatMutation = useStartChatMutation();

    const canStart = permissions.commands[AGGREGATE_NAMES.CHAT][CHAT_COMMANDS.startChat];
    const divisionId = user?.divisionId;
    const phoneNumberId = findOwnDivisionPhoneNumberId(corporation, divisionId);

    const kn1Applicant = lead?.antragsteller1;
    const kn2Applicant = lead?.antragsteller2;

    const kn1Resolved = useMemo<ResolveApplicantStartChatResult>(() => {
        if (!loanRequestId) return EMPTY_RESULT;
        return resolveApplicantStartChat({
            applicantPhone: kn1Applicant?.personendaten.telefonPrivat,
            applicantSlot: 1,
            canStart,
            dataProtectionAgreed: kn1Applicant?.dataProtectionAgreed,
            divisionId,
            loanRequestId,
            phoneNumberId,
        });
    }, [canStart, divisionId, kn1Applicant, loanRequestId, phoneNumberId]);

    const kn2Resolved = useMemo<ResolveApplicantStartChatResult>(() => {
        if (!loanRequestId) return EMPTY_RESULT;
        return resolveApplicantStartChat({
            applicantPhone: kn2Applicant?.personendaten.telefonPrivat,
            applicantSlot: 2,
            canStart,
            coApplicantHidden: lead?.coApplicantHidden,
            dataProtectionAgreed: kn2Applicant?.dataProtectionAgreed,
            divisionId,
            loanRequestId,
            phoneNumberId,
        });
    }, [canStart, divisionId, kn2Applicant, lead?.coApplicantHidden, loanRequestId, phoneNumberId]);

    const buildStart = (resolved: ResolveApplicantStartChatResult) => (): Promise<{ chatId: string }> => {
        if (!resolved.payload) {
            return Promise.reject(new Error('useApplicantStartChat: start() called without a resolved payload'));
        }
        return startChatMutation.mutateAsync(resolved.payload);
    };

    return {
        kn1: { ...kn1Resolved, isLoading, start: buildStart(kn1Resolved) },
        kn2: { ...kn2Resolved, isLoading, start: buildStart(kn2Resolved) },
    };
};
